import { gql, type TypedDocumentNode } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconGavel } from '@sorare/core/src/atoms/icons/system/iconGavel.ts';
import { Actions } from '@sorare/core/src/atoms/layout/Actions';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, HeadlineL } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import { ANY_SPORT_MARKET_SHOP_AUCTIONS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { css } from '@sorare/css';

import { EmptyLineupCards } from '#src/components/onboarding/EmptyLineupCards/index.tsx';
import pattern from '#src/components/onboarding/assets/pattern.svg';

import type { SignFirstPlayersDialog_careerBoard } from './__generated__/index.graphql.ts';

const illustration = css`
  position: relative;
  isolation: isolate;
  height: calc(22 * var(--unit));
  background:
    linear-gradient(rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.35)),
    linear-gradient(
      to bottom,
      var(--c-competition-jupiler-primary),
      var(--c-black) 98%
    );
  &:after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    opacity: 0.35;
    background: no-repeat center / cover url(${pattern});
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
  careerBoard?: SignFirstPlayersDialog_careerBoard | null;
};

export const SignFirstPlayersDialog = ({
  open,
  onClose,
  careerBoard = null,
}: Props) => {
  const { generateSportContextPath } = useSportContext();

  const auctionsPath = generateSportContextPath(ANY_SPORT_MARKET_SHOP_AUCTIONS);
  const boardFilterQuery = buildFilterQuery({
    [SEARCH_PARAMS.ALGOLIA_FILTERS]:
      careerBoard?.so5Competition?.algoliaFilters || undefined,
    [SEARCH_PARAMS.RARITY]: careerBoard?.rarities?.length
      ? careerBoard.rarities
      : undefined,
  });
  const filteredAuctionsPath = boardFilterQuery
    ? `${auctionsPath}?${boardFilterQuery}`
    : auctionsPath;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      fullScreen={false}
      hideHeader
      footer={
        <Actions direction="vertical" gap={1.5}>
          <Button
            color="tertiary"
            size="medium"
            selected
            accentRgb="var(--c-rgb-solana-primary)"
            startIcon={<FontAwesomeIcon icon={iconGavel} />}
            to={filteredAuctionsPath}
            onClick={onClose}
          >
            <FormattedMessage defaultMessage="Go to Auctions" />
          </Button>
          <Button size="medium" color="transparent" fullWidth onClick={onClose}>
            <FormattedMessage defaultMessage="Not now" />
          </Button>
        </Actions>
      }
    >
      <Horizontal center className={illustration}>
        <EmptyLineupCards />
      </Horizontal>
      <Vertical gap={1} className="px-3 pb-2">
        <HeadlineL>
          <FormattedMessage defaultMessage="Sign your first players" />
        </HeadlineL>
        <BodyM color="var(--c-nd-600)">
          <FormattedMessage defaultMessage="Head to the auctions and bid on your first players to get your team on the pitch." />
        </BodyM>
      </Vertical>
    </Dialog>
  );
};

SignFirstPlayersDialog.fragments = {
  careerBoard: gql`
    fragment SignFirstPlayersDialog_careerBoard on CareerBoard {
      id
      rarities
      so5Competition {
        slug
        algoliaFilters
      }
    }
  ` as TypedDocumentNode<SignFirstPlayersDialog_careerBoard>,
};
